'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore } from '@/lib/auth-store';
import type { Benutzer } from '@/lib/api/auth-types';
import {
  hatZugriffAufSeite as benutzerHatZugriffAufSeite,
  startseiteFuerBenutzer,
} from '@/lib/page-permissions';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Schützt alle Routen außer /login.
 * Prüft ob der Benutzer angemeldet ist und leitet bei Bedarf um.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { istAngemeldet, benutzer } = useAuthStore();
  const router = useRouter();
  const pathname = usePathname();
  const [bereit, setBereit] = useState(false);

  useEffect(() => {
    // Login-Seite braucht keinen Guard
    if (pathname === '/login') {
      setBereit(true);
      return;
    }

    if (!istAngemeldet) {
      router.replace('/login');
      return;
    }

    if (!benutzer) {
      router.replace('/login');
      return;
    }

    if (!hatZugriffAufSeite(benutzer, pathname)) {
      router.replace(startseiteFuerBenutzer(benutzer));
      return;
    }

    setBereit(true);
  }, [istAngemeldet, benutzer, pathname, router]);

  if (!bereit && pathname !== '/login') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Prüft ob der Benutzer Zugriff auf eine Seite hat.
 */
export function hatZugriffAufSeite(
  benutzer: Benutzer | null | undefined,
  pfad: string,
): boolean {
  return benutzerHatZugriffAufSeite(benutzer, pfad);
}
